import CustomButton from 'Components/CustomButton/CustomButton'
import CustomTablePagination from 'Components/CustomTablePagination'
import type { HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import EnhancedTableHead from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { SELECTED_SCHOOL_PARAMETER_KEY } from 'Services/ControlDataManagement/Features/School/Hooks/useSchoolsWithParams'
import {
  useDeleteSingleSchoolMutation,
  useGetSchoolsQuery
} from 'Services/ControlDataManagement/Features/School/schoolApi'
import type { FetchSchoolListParams } from 'Services/ControlDataManagement/Features/School/types'
import {
  CONTROL_DATA_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import { showValueOrHyphen } from 'Util/formatters'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography
} from '@mui/material'
import { Box } from '@mui/system'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

interface RelatedSchoolsProps {
  schoolId: string
}

export default function RelatedSchools({ schoolId }: Readonly<RelatedSchoolsProps>) {
  const baseUrl = `${CONTROL_DATA_MANAGEMENT_ROOT_URL}/school-management/schools`
  const navigate = useNavigate()
  const { confirm } = useConfirm()

  const headCells: HeadCell[] = [
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t`Unit's name`,
      sortable: true
    },
    {
      id: 'calendar',
      numeric: false,
      disablePadding: false,
      label: t`Unit's calendar`,
      sortable: false
    },
    {
      id: 'actions',
      numeric: false,
      disablePadding: false,
      label: t`Actions`,
      sortable: false
    }
  ]

  const [params, setParams] = useState<FetchSchoolListParams>({
    start_index: undefined,
    count: ROWS_PER_PAGE_DEFAULT,
    sort_by: 'name',
    sort_order: SORT_ORDER_ASC
  })
  const {
    data: { resources: schools } = { resources: [] },
    data: { total_results: totalResults } = { total_results: 0 },
    isFetching
  } = useGetSchoolsQuery({
    ...params,
    school_id: schoolId
  })

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const orderPrefix = `${params.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${params.sort_by}`

  const [deleteSingleSchool, { isLoading: isDeleteLoading }] = useDeleteSingleSchoolMutation()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const actionMenuOpen = Boolean(anchorEl)

  const openActionMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const closeActionMenu = () => {
    setAnchorEl(null)
  }

  const handleDelete = (schoolId: string) => {
    if (!schoolId) return

    deleteSingleSchool(schoolId)
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`School successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the school.`)
      })
  }

  const isLoading = isFetching || isDeleteLoading

  return (
    <Box>
      <WithLoader loading={isLoading}>
        <Paper sx={{ width: '100%', mb: 2, pt: 2 }}>
          <TableContainer>
            <Table aria-labelledby="tableTitle" size="small">
              <EnhancedTableHead
                orderBy={orderBy}
                headCells={headCells}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {schools.length > 0 ? (
                  schools.map(row => {
                    return (
                      <StyledTableRow tabIndex={-1} key={row.id}>
                        <NoWrapTableCell title={row.name}>
                          <Typography variant="body2">{showValueOrHyphen(row.name)}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.school_unit_id}
                          </Typography>
                        </NoWrapTableCell>
                        <NoWrapTableCell title={row.school_calendar.name}>
                          <Typography variant="body2">{row.school_calendar.name}</Typography>
                        </NoWrapTableCell>
                        <NoWrapTableCell title={t`Actions`}>
                          <IconButton onClick={openActionMenu}>
                            <MoreHorizIcon />
                          </IconButton>
                          <Menu anchorEl={anchorEl} onClose={closeActionMenu} open={actionMenuOpen}>
                            <MenuItem
                              key="edit"
                              onClick={() =>
                                navigate(
                                  `${CONTROL_DATA_MANAGEMENT_ROOT_URL}/school-management/schools/${row.id}/edit`
                                )
                              }
                              sx={{ color: 'primary.main' }}
                            >
                              <ListItemIcon>
                                <EditIcon sx={{ color: 'primary.main' }} />
                              </ListItemIcon>
                              {t`Edit`}
                            </MenuItem>
                            <MenuItem
                              key="delete"
                              sx={{ color: 'error.main' }}
                              onClick={() =>
                                confirm({
                                  title: t`Are you sure?`,
                                  message: t`Are you sure you want to delete the school?`,
                                  handleConfirm: () => {
                                    handleDelete(row.id)
                                    closeActionMenu()
                                  }
                                })
                              }
                            >
                              <ListItemIcon>
                                <DeleteIcon sx={{ color: 'error.main' }} />
                              </ListItemIcon>
                              {t`Delete`}
                            </MenuItem>
                          </Menu>
                        </NoWrapTableCell>
                      </StyledTableRow>
                    )
                  })
                ) : (
                  <StyledTableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2">{t`School has no related units.`}</Typography>
                    </TableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={totalResults}
            page={
              params.start_index
                ? Number.parseInt(params.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                : 0
            }
            onPageChange={handleChangePage}
          />
        </Paper>
        <CustomButton
          variant="text"
          size="small"
          startIcon={<AddIcon />}
          sx={{ ml: 2, mb: 2 }}
          onClick={() => navigate(`${baseUrl}/create?${SELECTED_SCHOOL_PARAMETER_KEY}=${schoolId}`)}
        >{t`Add unit`}</CustomButton>
      </WithLoader>
    </Box>
  )
}
